/**
 * DevSettings Component
 * Developer overlay for tweaking visual settings (F9 to toggle)
 */

import { useState, useEffect, useCallback } from 'react'
import './DevSettings.css'

export interface GlowSettings {
  emissiveIntensity: number
  coreBrightness: number  // 0-1, how much white to mix into core
  bloomIntensity: number
  bloomThreshold: number
  bloomRadius: number
  bloomSmoothing: number
}

interface DevSettingsProps {
  settings: GlowSettings
  onSettingsChange: (settings: GlowSettings) => void
  defaultSettings: GlowSettings
}

// Theme-specific default settings
export const LIGHT_MODE_SETTINGS: GlowSettings = {
  emissiveIntensity: 10.5,
  coreBrightness: 0,
  bloomIntensity: 0.4,
  bloomThreshold: 0.65,
  bloomRadius: 0.5,
  bloomSmoothing: 0,
}

export const DARK_MODE_SETTINGS: GlowSettings = {
  emissiveIntensity: 0.5,
  coreBrightness: 0,
  bloomIntensity: 0.5,
  bloomThreshold: 0.8,
  bloomRadius: 0.15,
  bloomSmoothing: 0,
}

export function useDevSettings(isLightMode: boolean = false) {
  const defaultSettings = isLightMode ? LIGHT_MODE_SETTINGS : DARK_MODE_SETTINGS

  const [settings, setSettings] = useState<GlowSettings>(() => {
    // Load from localStorage if available (theme-specific key)
    if (typeof window !== 'undefined') {
      const key = isLightMode ? 'devSettings_light' : 'devSettings_dark'
      const saved = localStorage.getItem(key)
      if (saved) {
        try {
          return { ...defaultSettings, ...JSON.parse(saved) }
        } catch {
          return defaultSettings
        }
      }
    }
    return defaultSettings
  })

  // Update settings when theme changes
  useEffect(() => {
    const key = isLightMode ? 'devSettings_light' : 'devSettings_dark'
    const saved = localStorage.getItem(key)
    if (saved) {
      try {
        setSettings({ ...defaultSettings, ...JSON.parse(saved) })
      } catch {
        setSettings(defaultSettings)
      }
    } else {
      setSettings(defaultSettings)
    }
  }, [isLightMode])

  const updateSettings = useCallback((newSettings: GlowSettings) => {
    setSettings(newSettings)
    const key = isLightMode ? 'devSettings_light' : 'devSettings_dark'
    localStorage.setItem(key, JSON.stringify(newSettings))
  }, [isLightMode])

  const resetSettings = useCallback(() => {
    setSettings(defaultSettings)
    const key = isLightMode ? 'devSettings_light' : 'devSettings_dark'
    localStorage.removeItem(key)
  }, [isLightMode, defaultSettings])

  return { settings, updateSettings, resetSettings, defaultSettings }
}

export function DevSettings({ settings, onSettingsChange, defaultSettings }: DevSettingsProps) {
  const [isOpen, setIsOpen] = useState(false)

  // F9 to toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F9') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleChange = (key: keyof GlowSettings, value: number) => {
    onSettingsChange({ ...settings, [key]: value })
  }

  const handleReset = () => {
    onSettingsChange(defaultSettings)
  }

  if (!isOpen) return null

  return (
    <div className="dev-settings-overlay">
      <div className="dev-settings-panel">
        <div className="dev-settings-header">
          <h3>Dev Settings (F9)</h3>
          <button className="dev-close-btn" onClick={() => setIsOpen(false)}>×</button>
        </div>

        <div className="dev-settings-content">
          {/* Glow Shape Settings */}
          <div className="dev-section">
            <h4>Glow Shape</h4>

            <div className="dev-control">
              <label>
                Emissive Intensity: {settings.emissiveIntensity.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="20"
                step="0.5"
                value={settings.emissiveIntensity}
                onChange={(e) => handleChange('emissiveIntensity', parseFloat(e.target.value))}
              />
            </div>

            <div className="dev-control">
              <label>
                Core Brightness: {(settings.coreBrightness * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.coreBrightness}
                onChange={(e) => handleChange('coreBrightness', parseFloat(e.target.value))}
              />
            </div>
          </div>

          {/* Bloom Settings */}
          <div className="dev-section">
            <h4>Bloom Post-Processing</h4>

            <div className="dev-control">
              <label>
                Intensity: {settings.bloomIntensity.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="5"
                step="0.1"
                value={settings.bloomIntensity}
                onChange={(e) => handleChange('bloomIntensity', parseFloat(e.target.value))}
              />
            </div>

            <div className="dev-control">
              <label>
                Threshold: {settings.bloomThreshold.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.bloomThreshold}
                onChange={(e) => handleChange('bloomThreshold', parseFloat(e.target.value))}
              />
            </div>

            <div className="dev-control">
              <label>
                Radius: {settings.bloomRadius.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={settings.bloomRadius}
                onChange={(e) => handleChange('bloomRadius', parseFloat(e.target.value))}
              />
            </div>

            <div className="dev-control">
              <label>
                Smoothing: {settings.bloomSmoothing.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.bloomSmoothing}
                onChange={(e) => handleChange('bloomSmoothing', parseFloat(e.target.value))}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="dev-actions">
            <button className="dev-reset-btn" onClick={handleReset}>
              Reset to Defaults
            </button>
            <div className="dev-current-values">
              <code>{JSON.stringify(settings, null, 2)}</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
